'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import NewsletterForm from '@/ui/segments/landing/components/coming-soon/newsletter-form';
import ProgressiveImage from '@/ui/segments/landing/components/progressive-image/progressive-image';
import { EnumSection } from '@/ui/segments/landing/sections/sections';
import { getSection } from '@/ui/segments/landing/utils';
import { classNames } from '@/util/utils';
import useFullHeight from '@/utils/use-full-height';
import NextPanel from './next-panel';
import type { ContentForHero } from '@/services/sanity/api/get-hero-content';
import styles from './hero.module.css';
interface HeroClientProps {
  className?: string;
  section: EnumSection;
  data: ContentForHero;
}
export default function HeroClient(t0) {
  const $ = _c(36);
  const {
    className,
    section,
    data
  } = t0;
  const {
    title,
    backgroundType,
    imageURL,
    videoURL,
    posterURL,
    posterWidth,
    posterHeight,
    content,
    next
  } = data;
  const [videoReady, setVideoReady] = React.useState(false);
  const height = useFullHeight();
  let t1;
  if ($[0] !== section) {
    t1 = getSection(section).slug.replace(/^\//, "") || "home";
    $[0] = section;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sectionSlug = t1;
  const heroTestIdPrefix = `landing-${sectionSlug}-hero`;
  let t2;
  if ($[2] !== className) {
    t2 = classNames(className, styles.hero);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== height) {
    t3 = {
      height
    };
    $[4] = height;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = classNames(styles.background);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== backgroundType || $[8] !== heroTestIdPrefix || $[9] !== posterHeight || $[10] !== posterURL || $[11] !== posterWidth || $[12] !== videoReady || $[13] !== videoURL) {
    t5 = backgroundType === "video" && <>{posterURL && posterWidth && posterHeight && <ProgressiveImage src={posterURL} width={posterWidth} height={posterHeight} alt="Hero image" testId={`${heroTestIdPrefix}-image`} />}<video className={videoReady ? styles.show : styles.hide} loop={true} muted={true} autoPlay={true} playsInline={true} disablePictureInPicture={true} src={videoURL ?? ""} onCanPlay={() => setVideoReady(true)} /></>;
    $[7] = backgroundType;
    $[8] = heroTestIdPrefix;
    $[9] = posterHeight;
    $[10] = posterURL;
    $[11] = posterWidth;
    $[12] = videoReady;
    $[13] = videoURL;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== backgroundType || $[16] !== heroTestIdPrefix || $[17] !== imageURL) {
    t6 = backgroundType === "image" && imageURL && <Image src={imageURL} alt="" fill={true} sizes="100vw" preload={true} data-testid={`${heroTestIdPrefix}-image`} />;
    $[15] = backgroundType;
    $[16] = heroTestIdPrefix;
    $[17] = imageURL;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== content || $[23] !== heroTestIdPrefix || $[24] !== section || $[25] !== title) {
    t8 = section === EnumSection.ComingSoon ? <div className={styles.comingSoon}><ComingSoon /></div> : <div className={styles.text}><div className="flex flex-col items-center"><h1 className={styles.largeTitle} data-testid={section === EnumSection.Home ? "home-hero-heading" : `${heroTestIdPrefix}-heading`}>{title}</h1>{section === EnumSection.Home && <Link href="/app/virtual-lab/sync" className={styles.virtualLabsButton} data-testid="virtual-labs-link"><div className="font-title relative top-0.5 text-lg text-white md:text-xl">Go to</div><div className="relative -top-0.5 text-center font-serif text-4xl whitespace-nowrap text-white md:text-5xl">Virtual Labs</div></Link>}{content && <div className={styles.content}>{content}</div>}</div></div>;
    $[22] = content;
    $[23] = heroTestIdPrefix;
    $[24] = section;
    $[25] = title;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = `${heroTestIdPrefix}-next`;
  let t10;
  if ($[27] !== next || $[28] !== t9) {
    t10 = <footer><NextPanel testId={t9}>{next}</NextPanel></footer>;
    $[27] = next;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t2 || $[32] !== t3 || $[33] !== t7 || $[34] !== t8) {
    t11 = <div className={t2} style={t3}>{t7}{t8}{t10}</div>;
    $[30] = t10;
    $[31] = t2;
    $[32] = t3;
    $[33] = t7;
    $[34] = t8;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function ComingSoon() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = classNames("relative mx-auto min-h-[calc(100svh-120px)] max-w-3xl", "flex flex-col items-center justify-center", "px-6 sm:px-12");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <main className={t0}><div className="flex flex-col items-start justify-start"><div className="animate-fade-in mb-6"><h1 className="mb-4 font-serif text-4xl font-bold tracking-[0.040em] text-white select-none sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">Releasing soon</h1><p className="mx-auto mr-4 max-w-3xl text-justify text-lg font-medium text-white select-none sm:text-left md:text-xl">The platform will be launched soon with access to features such as single cell modeling and experiment design in addition to access to notebooks</p></div><NewsletterForm position="page" key="main-newsletter-form" /></div></main>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}